/**
 * Checks if annotation data differs from what's already on the slide
 * @param {Object} slide - The slide to check
 * @param {Object} annotation - The annotation data to compare against
 * @returns {Boolean} - Whether the annotation data is different
 */
function isAnnotationDifferent(slide, annotation) {
  // If annotation is null/undefined, consider it not a change (no update needed)
  if (!annotation) {
    return false
  }

  // If any annotation property doesn't exist or is falsy on the slide, consider it a change
  if (!slide.annotationType ||
      !slide.annotationSource ||
      slide.annotationIsVisible === undefined) {
    return true
  }

  return slide.annotationType !== annotation.annotationType ||
         slide.annotationSource !== annotation.source ||
         slide.annotationIsVisible !== annotation.isVisible
}

/**
 * Synchronizes slides with their annotation data
 * @param {Array} slides - Original slides array
 * @param {Object} annotations - Annotation data keyed by slideId
 * @returns {Object} - Updated slides and change status
 */
export function syncSlidesWithAnnotations(slides, annotations) {
  const updatedSlides = [...slides]
  let hasChanges = false

  // Get all slide IDs that have annotations
  const slideIdsWithAnnotations = Object.keys(annotations)

  // Process each slide that has an annotation
  slideIdsWithAnnotations.forEach((slideId) => {
    const annotation = annotations[slideId]

    // Convert slideId to number for comparison since IDs from object keys are strings
    const slideIdNum = Number(slideId)
    const slideIndex = updatedSlides.findIndex((slide) => slide?.id === slideIdNum)

    // Skip if slide not found
    if (slideIndex === -1) return

    const currentSlide = updatedSlides[slideIndex]
    const needsUpdate = isAnnotationDifferent(currentSlide, annotation)

    if (needsUpdate) {
      updatedSlides[slideIndex] = {
        ...currentSlide,
        annotationType: annotation?.annotationType,
        annotationSource: annotation?.source,
        annotationIsVisible: annotation?.isVisible,
      }
      hasChanges = true
    }
  })

  return { updatedSlides, hasChanges }
}
